import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Heart,
  AlertTriangle,
  Syringe,
  Activity,
  Eye,
  MapPin,
  Info,
  Shield,
  Zap,
  CheckCircle2
} from 'lucide-react';

export default function PonctionPericardiquePage() {
  const [checkedItems, setCheckedItems] = useState({});

  const handleCheck = (section, item) => {
    setCheckedItems(prev => ({
      ...prev,
      [`${section}-${item}`]: !prev[`${section}-${item}`]
    }));
  };

  const sectionsData = [
    {
      id: 'generalites',
      number: '01',
      title: 'Généralités',
      icon: Info,
      color: 'text-cyan-400',
      items: [
        { 
          label: 'Péricardiocentèse sous-xyphoïdienne',
          detail: 'Technique de référence en urgence'
        },
        { 
          label: 'Position semi-assise',
          detail: 'Patient installé en position semi-assise',
          critical: true
        },
        { 
          label: 'Remplissage vasculaire en cours',
          detail: 'Maintenir la précharge pendant la procédure',
          critical: true
        },
        { 
          label: 'Protocole "tamponnade"',
          detail: 'À rapprocher du protocole de prise en charge de la tamponnade'
        },
        { 
          label: 'Respecter ventilation spontanée',
          detail: 'Ne pas intuber si possible',
          critical: true
        },
        { 
          label: 'Respecter la tachycardie',
          detail: 'Mécanisme compensatoire à ne pas traiter',
          critical: true
        }
      ]
    },
    {
      id: 'indications',
      number: '02',
      title: 'Indications',
      icon: Heart,
      color: 'text-red-400',
      items: [
        { 
          label: 'Tamponnade mal supportée',
          detail: 'Signes de choc, hypotension, bas débit',
          critical: true
        },
        { 
          label: 'En attente drainage chirurgical',
          detail: 'Geste temporaire avant transport vers chirurgie',
          critical: true
        }
      ]
    },
    {
      id: 'technique',
      number: '03',
      title: 'Technique',
      icon: Syringe,
      color: 'text-purple-400',
      subsections: [
        {
          titre: 'Asepsie',
          items: [
            { 
              label: 'Asepsie iodée',
              detail: 'Désinfection large à la Bétadine®',
              critical: true
            },
            { 
              label: 'Champs stériles',
              detail: 'Mise en place de champs stériles'
            },
            { 
              label: 'Gants stériles',
              detail: 'Obligatoire pour le praticien'
            }
          ]
        },
        {
          titre: 'Anesthésie Locale',
          items: [
            { 
              label: 'Angle costo-xyphoïdien gauche (point de Marfan)',
              detail: 'Infiltration de Xylocaïne® 1-2%',
              critical: true
            },
            { 
              label: 'SAUF en cas d\'urgence vitale',
              detail: 'Si ACR imminent, pas d\'anesthésie',
              critical: true
            }
          ]
        },
        {
          titre: 'Matériel',
          items: [
            { 
              label: 'Seringue de 50 ml',
              detail: 'Pour aspiration du liquide',
              critical: true
            },
            { 
              label: 'Robinets 3 voies',
              detail: 'Permettre aspiration continue'
            },
            { 
              label: '1 aiguille IM',
              detail: 'Aiguille longue de ponction'
            }
          ]
        },
        {
          titre: 'Méthode',
          items: [
            { 
              label: 'Site de ponction : Angle xypho-costal gauche',
              detail: 'Point de Marfan',
              critical: true
            },
            { 
              label: 'Angle : 45° du plan cutané',
              detail: 'Par rapport à la peau',
              critical: true
            },
            { 
              label: 'Direction : Visant la médioclavicule gauche',
              detail: 'Ou région médio-claviculaire gauche',
              critical: true
            },
            { 
              label: 'Position du patient : Semi-assise',
              detail: 'Améliore l\'accès et la tolérance'
            },
            { 
              label: 'Progression lente, le vide à la main',
              detail: 'Aspirer en avançant doucement',
              critical: true
            },
            { 
              label: 'Émission de liquide séreux, séro-hématique ou sanglant',
              detail: 'Traduit la localisation péricardique du biseau',
              critical: true
            },
            { 
              label: 'L\'aspiration doit s\'accompagner d\'une amélioration clinique quasi immédiate',
              detail: 'Augmentation TA, diminution FC',
              critical: true
            },
            { 
              label: 'Retrait de 20 à 50 ml',
              detail: 'Généralement suffisant pour lever la tamponnade',
              critical: true
            }
          ]
        }
      ]
    },
    {
      id: 'voie',
      number: '03',
      title: 'Voie d\'Abord - Approche Sous-Xiphoïdienne',
      icon: MapPin,
      color: 'text-amber-400',
      content: {
        description: "L'approche sous-xiphoïdienne (ou voie de Marfan) est la voie de référence en urgence",
        reperes: [
          {
            title: 'Point de ponction',
            detail: '1 cm en dessous et à gauche de l\'appendice xiphoïde',
            important: true
          },
          {
            title: 'Angle d\'insertion',
            detail: '30-45° par rapport à la peau, aiguille dirigée vers l\'épaule gauche'
          },
          {
            title: 'Direction',
            detail: 'Viser la pointe de l\'omoplate gauche ou l\'épaule gauche'
          },
          {
            title: 'Profondeur',
            detail: 'Variable selon morphologie, généralement 5-8 cm'
          }
        ]
      }
    },
    {
      id: 'technique',
      number: '04',
      title: 'Technique de Ponction',
      icon: Syringe,
      color: 'text-purple-400',
      steps: [
        {
          numero: '1',
          titre: 'Installation et préparation',
          items: [
            { label: 'Patient en position semi-assise (30-45°)', detail: 'Si état hémodynamique le permet' },
            { label: 'Monitoring continu : Scope, PNI, SpO2', detail: 'Obligatoire', critical: true },
            { label: 'Désinfection large de la zone sous-xiphoïdienne', detail: 'Bétadine®' },
            { label: 'Pose de champs stériles', detail: 'Asepsie stricte' }
          ]
        },
        {
          numero: '2',
          titre: 'Anesthésie locale (si patient conscient)',
          items: [
            { label: 'Infiltration de Xylocaïne® 1-2%', detail: 'Sous-cutané puis plus profond' },
            { label: 'Anesthésier tout le trajet prévu', detail: 'Jusqu\'au péricarde' }
          ]
        },
        {
          numero: '3',
          titre: 'Insertion de l\'aiguille',
          items: [
            { label: 'Aiguille 16-18G connectée à une seringue', detail: '20-50 ml avec robinet 3 voies', critical: true },
            { label: 'Introduire 1 cm sous et à gauche de l\'appendice xiphoïde', detail: 'Angle 30-45°' },
            { label: 'Direction : vers l\'épaule gauche', detail: 'Légèrement en arrière', critical: true },
            { label: 'Progression lente en aspirant continuellement', detail: 'Aspiration douce pendant l\'avancée', critical: true }
          ]
        },
        {
          numero: '4',
          titre: 'Monitoring ECG (pince crocodile)',
          items: [
            { label: 'Connecter une électrode ECG à l\'aiguille', detail: 'Via pince crocodile' },
            { label: 'Surveillance du tracé : sus-décalage ST = contact myocardique', detail: 'ARRÊTER et retirer légèrement', critical: true },
            { label: 'Apparition du liquide = péricarde atteint', detail: 'Aspirer doucement' }
          ]
        },
        {
          numero: '5',
          titre: 'Aspiration du liquide',
          items: [
            { label: 'Aspirer 20-50 ml minimum', detail: 'Souvent suffisant pour lever la tamponnade' },
            { label: 'Continuer tant que liquide obtenu', detail: 'Et amélioration clinique visible' },
            { label: 'Conserver le liquide pour analyses', detail: 'Biochimie, cytologie, bactériologie', critical: true }
          ]
        },
        {
          numero: '6',
          titre: 'Retrait et surveillance',
          items: [
            { label: 'Retirer l\'aiguille lentement', detail: 'En aspirant' },
            { label: 'Pansement compressif du point de ponction', detail: '' },
            { label: 'Surveillance continue post-ponction', detail: 'Scope, TA, SpO2', critical: true }
          ]
        }
      ]
    },
    {
      id: 'guidage-echo',
      number: '05',
      title: 'Guidage Échographique (Recommandé)',
      icon: Eye,
      color: 'text-blue-400',
      content: {
        avantages: [
          'Visualisation de l\'épanchement péricardique',
          'Repérage du meilleur point de ponction',
          'Visualisation en temps réel du trajet de l\'aiguille',
          'Évite les structures vitales (cœur, foie, vaisseaux)',
          'Améliore le taux de succès et réduit les complications'
        ],
        technique: [
          'Sonde cardiaque en position sous-xiphoïdienne',
          'Identifier l\'épanchement (zone anéchogène)',
          'Choisir la fenêtre avec le plus d\'épanchement',
          'Suivre le trajet de l\'aiguille en temps réel',
          'Visualiser l\'aspiration du liquide'
        ]
      }
    },
    {
      id: 'surveillance',
      number: '04',
      title: 'Surveillance',
      icon: Activity,
      color: 'text-green-400',
      items: [
        { 
          label: 'Surveillance continue de la TA',
          detail: 'Augmentation attendue après aspiration',
          critical: true
        },
        { 
          label: 'Surveillance continue de l\'ECG',
          detail: 'Détection troubles du rythme',
          critical: true
        },
        { 
          label: 'Amélioration clinique quasi immédiate',
          detail: 'Si tamponnade levée efficacement',
          critical: true
        }
      ]
    },
    {
      id: 'complications',
      number: '05',
      title: 'Complications',
      icon: AlertTriangle,
      color: 'text-red-400',
      items: [
        { 
          label: 'Troubles du rythme',
          detail: 'Tachycardie, fibrillation, extrasystoles',
          severity: 'critical'
        },
        { 
          label: 'Plaie ventriculaire',
          detail: 'Ponction du ventricule, hémopéricarde iatrogène',
          severity: 'critical'
        },
        { 
          label: 'Déchirure de l\'artère coronaire',
          detail: 'Hémorragie intrapéricardique massive',
          severity: 'critical'
        },
        { 
          label: 'PNO (Pneumothorax)',
          detail: 'Si déviation latérale de l\'aiguille',
          severity: 'high'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--text))] p-4 md:p-6" data-testid="ponction-pericardique-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-3">
            <Heart className="w-10 h-10 text-red-400" />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-red-400 to-red-300 bg-clip-text text-transparent">
              Ponction Péricardique
            </h1>
          </div>
          <p className="text-[hsl(var(--text-dim))] text-sm md:text-base max-w-3xl mx-auto">
            Protocole complet de ponction péricardique en urgence (voie sous-xiphoïdienne)
          </p>
        </div>

        {/* Alerte Urgence */}
        <Alert className="bg-red-500/10 border-red-500/30 border-2">
          <AlertTriangle className="w-5 h-5 text-red-400" />
          <AlertDescription>
            <p className="font-bold text-red-300 text-lg mb-2">⚠️ GESTE DE SAUVETAGE VITAL</p>
            <p className="text-[hsl(var(--text))]">
              La ponction péricardique est un geste d'extrême urgence réservé aux tamponnades avec signes de gravité (choc, ACR imminent). 
              Guidage échographique fortement recommandé si disponible.
            </p>
          </AlertDescription>
        </Alert>

        {/* Accordéons */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <Activity className="w-5 h-5" />
              Guide Complet de la Ponction Péricardique
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" className="space-y-4">
              {sectionsData.map((section) => {
                const SectionIcon = section.icon;
                
                return (
                  <AccordionItem 
                    key={section.id} 
                    value={section.id}
                    className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]"
                    data-testid={`section-${section.id}`}
                  >
                    <AccordionTrigger className="px-4 py-3 hover:bg-[hsl(var(--bg-elev))] transition-colors">
                      <div className="flex items-center gap-3">
                        <Badge className="bg-cyan-500 text-white font-bold">{section.number}</Badge>
                        <SectionIcon className={`w-5 h-5 ${section.color}`} />
                        <span className="font-semibold text-[hsl(var(--text))]">{section.title}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="mt-3 space-y-4">
                        {/* Items avec checkboxes */}
                        {section.items && (
                          <div className="space-y-3">
                            {section.items.map((item, idx) => (
                              <div 
                                key={idx}
                                className={`flex items-start gap-3 p-3 rounded-lg border ${
                                  item.critical || item.important
                                    ? 'bg-amber-500/10 border-amber-500/30'
                                    : item.severity === 'critical'
                                      ? 'bg-red-500/10 border-red-500/30'
                                      : item.severity === 'high'
                                        ? 'bg-orange-500/10 border-orange-500/30'
                                        : 'bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]'
                                }`}
                              >
                                <Checkbox
                                  id={`${section.id}-${idx}`}
                                  checked={checkedItems[`${section.id}-${idx}`] || false}
                                  onCheckedChange={() => handleCheck(section.id, idx)}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <label 
                                    htmlFor={`${section.id}-${idx}`}
                                    className="font-medium text-[hsl(var(--text))] cursor-pointer block"
                                  >
                                    {item.label}
                                  </label>
                                  {item.detail && (
                                    <p className="text-sm text-[hsl(var(--text-dim))] mt-1">{item.detail}</p>
                                  )}
                                  {(item.critical || item.important) && (
                                    <Badge variant="outline" className="mt-2 border-amber-500/30 text-amber-300 text-xs">
                                      <AlertTriangle className="w-3 h-3 mr-1" />
                                      Critique
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Voie d'abord */}
                        {section.id === 'voie' && section.content && (
                          <>
                            <Alert className="bg-amber-500/10 border-amber-500/30">
                              <MapPin className="w-4 h-4 text-amber-400" />
                              <AlertDescription className="text-[hsl(var(--text))]">
                                {section.content.description}
                              </AlertDescription>
                            </Alert>
                            <div className="space-y-3">
                              {section.content.reperes.map((repere, idx) => (
                                <div 
                                  key={idx}
                                  className={`p-4 rounded-lg ${
                                    repere.important 
                                      ? 'bg-cyan-500/10 border border-cyan-500/30' 
                                      : 'bg-[hsl(var(--bg-elev))]'
                                  }`}
                                >
                                  <p className="font-semibold text-[hsl(var(--text))] mb-1">{repere.title}</p>
                                  <p className="text-sm text-[hsl(var(--text-dim))]">{repere.detail}</p>
                                </div>
                              ))}
                            </div>
                          </>
                        )}

                        {/* Technique par subsections */}
                        {section.id === 'technique' && section.subsections && (
                          <div className="space-y-4">
                            {section.subsections.map((subsec, idx) => (
                              <div key={idx} className="border border-[hsl(var(--border))] rounded-lg p-4">
                                <h4 className="font-semibold text-purple-300 mb-3">{subsec.titre}</h4>
                                <div className="space-y-2">
                                  {subsec.items.map((item, itemIdx) => (
                                    <div 
                                      key={itemIdx}
                                      className={`flex items-start gap-3 p-3 rounded-lg ${
                                        item.critical 
                                          ? 'bg-amber-500/10 border border-amber-500/30' 
                                          : 'bg-[hsl(var(--bg-elev))]'
                                      }`}
                                    >
                                      <Checkbox
                                        id={`${section.id}-${idx}-${itemIdx}`}
                                        checked={checkedItems[`${section.id}-${idx}-${itemIdx}`] || false}
                                        onCheckedChange={() => handleCheck(section.id, `${idx}-${itemIdx}`)}
                                        className="mt-1"
                                      />
                                      <div className="flex-1">
                                        <label 
                                          htmlFor={`${section.id}-${idx}-${itemIdx}`}
                                          className="font-medium text-[hsl(var(--text))] cursor-pointer block text-sm"
                                        >
                                          {item.label}
                                        </label>
                                        {item.detail && (
                                          <p className="text-xs text-[hsl(var(--text-dim))] mt-1">{item.detail}</p>
                                        )}
                                        {item.critical && (
                                          <Badge variant="outline" className="mt-2 border-amber-500/30 text-amber-300 text-xs">
                                            <AlertTriangle className="w-3 h-3 mr-1" />
                                            Critique
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Pas de section guidage echo dans ce protocole */}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
